import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RouteTypes } from '~/routes/constants';
import dayjs from 'dayjs';
import { Divider, Form, Tab, TabPane } from 'semantic-ui-react';
import Layout from '~/components/Layout';
import notify from '~/components/Notify';
import { FormFields } from '~/components/FormFields';
import NewsletterService from '~/api/services/newsletter';
import { SaveDraftButton } from '~/pages/Dairy/Newsletter/style';
import { NewsletterQuery } from '~/QueryHooks/NewsLetterQuery';
import { DateTimeInput } from 'semantic-ui-calendar-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '~/routes/components/AuthProvider';
import { El } from '~/components/Layout/style';
import { StyledMenuItem } from '../../Settings/General/style';
import { convertDate, handleErrors } from '~/utils/helpers';
import SendNewsletterDialog from './SendNewsletterDialog';
import Recipients from './Recipients';
import LangTab from '~/pages/Dairy/Newsletter/CreateEditNewsletter/LangTab';
import { CLIMATE_CHECK_SLUG, CO2_LFL_SLUG, FAT_PROTEIN_SLUG, MILK_FIXED_SLUG, MILK_STOCK_EXCHANGE } from '~/Constants';

const watchFields = ['schedule', 'sendDate', 'type', 'isDraft', 'farmerGroups']
const filterModulesSlug = [MILK_FIXED_SLUG, FAT_PROTEIN_SLUG, MILK_STOCK_EXCHANGE, CO2_LFL_SLUG, CLIMATE_CHECK_SLUG] as const

export type FilterModuleSlug = typeof filterModulesSlug[number];

const CreateEditNewsletter: React.FC = () => {
  const { systemData, systemData: { languages, logged: { languageId }, modules } } = useAuth();
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate()
  const languageLocal = languages.find(language => language.id === languageId);
  const tfaNewsletterSend = systemData?.roleSettings?.tfaNewsletterSend === 1
  const filterModules = modules.filter(module => !!filterModulesSlug.find(tradingSlotSlug => tradingSlotSlug === module.slug))
  const recipientsRef = useRef<HTMLDivElement>(null)

  const [isBusy, setIsBusy] = useState(false);
  const [activeIndex, setActiveIndex] = useState<string | number>(0);
  const [isNewsletterDialogOpen, setIsNewsletterDialogOpen] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState<FilterModuleSlug>(filterModules[0]?.slug as FilterModuleSlug)
  const [startingDraftValue, setStartingDraftValue] = useState(0)

  const formMethods = useForm({
    mode: 'onSubmit'
  });

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState
  } = formMethods

  const [
    watchedSchedule,
    watchSendDate,
    watchType
  ] = watchFields.map(fieldName => useWatch({ control, name: fieldName }))

  const isDraft = formMethods.watch('isDraft')

  const {
    data: newsletterOptions
  } = NewsletterQuery.getNewsLetterOptions();

  useEffect(() => {
    if (id) {
      fetchNewsletterData();
    } else {
      setStartingDraftValue(1)
      setValue('type', 'intern')
    }
  }, []);

  useEffect(() => {
    if (watchType === 'intern') {
      setValue('additionalRecipients', [])
    }
    if (watchedSchedule === 0) {
      setValue('sendDate', null)
    }
  }, [watchType, watchedSchedule])

  async function fetchNewsletterData() {
    setIsBusy(true);
    try {
      const { data } = await NewsletterService.editNewsletter(id)
      data.additionalRecipients = data.additionalRecipients.map(recipient => ({ email: recipient }))
      data.sendDate = data.sendDate ? dayjs(new Date(data.sendDate)).format('DD.MM.YYYY HH:mm') : data.sendDate
      languages.map((language) => {
        const index = data.languages.findIndex(languageText => languageText.id === language.id)
        if (index < 0) {
          data.languages.push({
            id: language.id
          })
        } else {
          data.languages[index].order = language.order
        }
      })
      //reverse languages by order
      data?.languages?.sort((a, b) => a.order - b.order)
      data?.languages?.forEach(lang => delete lang.order)
      setStartingDraftValue(data.isDraft)
      reset(data);
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false);
  }

  function errorTab() {
    //@ts-ignore
    return formState.errors?.languages?.map((lang, index) => index)?.filter(item => typeof (item) === 'number')
  }

  function getMenuItem(label, index) {
    const errors = errorTab() || []

    return <StyledMenuItem
      key={label}
      className='tab-item'
    >
      <div className={`${errors.includes(index) ? 'error' : ''}`}>{label}</div>
    </StyledMenuItem>
  }

  function renderLangTab(languageItem, languageIndex) {
    const languages = getValues('languages') || []
    const indexLanguage = languages.findIndex(language => language.id === languageItem.id)
    const addEmailCheckBox = watchType && watchType !== 'intern'
    return <>
      <LangTab
        language={languageItem}
        languageIndex={indexLanguage < 0 ? languageIndex : indexLanguage}
      />
      {addEmailCheckBox && <>
        <Divider />
        <FormFields.Checkbox
          control={control}
          label={t('Use default email signature')}
          checkboxValue={1}
          defaultValue={0}
          name={'useDefaultSignature'}
          rules={{
            required: {
              value: Boolean(isDraft),
              message: t('Please enter date')
            }
          }}
        />
      </>
      }
    </>
  }

  function renderTabs() {
    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        renderActiveOnly={false}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
        panes={newsletterOptions?.languages?.map((language, languageIndex) => {
          return {
            menuItem: getMenuItem(language.label, languageIndex) || null,
            pane: <TabPane
              active={languageIndex === activeIndex}
              style={{ border: 'none' }}
              key={language?.id}
            >
              {renderLangTab(language, languageIndex)}
            </TabPane>
          };
        })}
      />
    );
  }

  function removeIfNotBase64(languageArray) {
    languageArray.forEach(language => {
      language.attachments = language?.attachments?.filter((attachment) => !!attachment.attachment.startsWith('data:')) || []
    })
    return languageArray
  }

  async function onSubmit(data, e, confirmButton?) {
    if (tfaNewsletterSend && !confirmButton) {
      setIsNewsletterDialogOpen(true)
    } else {
      data.schedule = watchedSchedule && watchedSchedule > 0 ? 1 : 0
      //remove attachment if it is not base64 (api issue)
      data.languages = removeIfNotBase64(data.languages);
      data.isDraft = data.isDraft ?? 0
      data.additionalRecipients = data.additionalRecipients?.map(recipient => recipient.email)
      data.useDefaultSignature = data.useDefaultSignature ? 1 : 0
      if (data.sendDate) {
        data.sendDate = convertDate(data.sendDate)
      }
      const { id, ...rest } = data;
      const newsletterData = { ...rest, isDraft: data.isDraft ?? 0 };
      setIsBusy(true);
      try {
        if (id) {
          await NewsletterService.updateNewsletter(id, newsletterData);
          if (newsletterData.isDraft === 0 && newsletterData.schedule === 1) {
            notify(t('The sending of the newsletter was successfully planned'), 'success')
          } else {
            notify(isDraft ? t('Newsletter draft updated') : t('Newsletter sent'), 'success');
          }
        } else {
          await NewsletterService.createNewsletter(newsletterData);
          if (newsletterData.isDraft === 0 && newsletterData.schedule === 1) {
            notify(t('The sending of the newsletter was successfully planned'), 'success')
          } else {
            notify(isDraft ? t('Successfully created newsletter draft') : t('Newsletter sent'), 'success');
          }
        }
        setIsBusy(false);
        navigate(RouteTypes.NEWSLETTER);
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false);
    }
  }

  function renderSendNewsLetterModal() {
    return <SendNewsletterDialog
      isDialogOpen={isNewsletterDialogOpen}
      isDraft={isDraft}
      scheduled={watchSendDate}
      recipientsRef={recipientsRef}
      watch={formMethods.watch}
      handleClose={() => setIsNewsletterDialogOpen(false)}
      onSubmit={(code) => onSubmit({
        ...getValues(),
        code
      }, null, true)}
    />
  }

  function renderScheduleFields() {
    return <FormFields.External
      control={control}
      name={'sendDate'}
      label={t('Send on')}
      onChange={(event, { name, value }) => setValue('sendDate', value)}
      rules={{
        required: {
          value: watchedSchedule && watchedSchedule > 0,
          message: t('Please enter date')
        }
      }}
    >
      <DateTimeInput
        name='dateTime'
        className={'datePicker'}
        minDate={dayjs(new Date()).format('DD.MM.YYYY HH:mm')}
        placeholder={t('Date time')}
        closable
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        onKeyPress={(event) => event.preventDefault()}
        dateTimeFormat={'DD.MM.YYYY HH:mm'}
        value={watchSendDate}
        iconPosition='left'
        onChange={(event, { name, value }) => setValue('sendDate', value)}
      />
    </FormFields.External>
  }

  function renderForm() {
    return <>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          label={t('Type')}
          placeholder={t('Type')}
          name={'type'}
          options={newsletterOptions?.types?.map(type => ({ value: type.key, text: type.label }))}
        />
        <FormFields.Checkbox
          name={'schedule'}
          label={t('Schedule')}
          style={{ marginTop: 34 }}
          checkboxValue={1}
          defaultValue={0}
          control={control}
        />
        {Boolean(watchedSchedule) && renderScheduleFields()}
        {filterModules.length > 1 && <Form.Dropdown
          label={t('Filters')}
          placeholder={t('Filters')}
          selectOnBlur={false}
          value={selectedFilter}
          selection
          options={filterModules.map(filter => ({ value: filter.slug, text: filter.label }))}
          onChange={(e, data) => {
            reset({
              ...getValues(),
              farmerGroups: '',
              tradingSlotsConfirmed: null,
              tradingSlotsOffered: null,
              co2Footprints: null,
              co2FootprintAnswered: null,
              climateCheckFootprints: null,
              climateCheckFootprintAnswered: null,
              fatProteinTradingSlotsOffered: null,
              fatProteinTradingSlotsConfirmed: null,
              stockExchangeTradingSlotsOffered: null,
              stockExchangeTradingSlotsConfirmed: null
            })
            //@ts-ignore
            setSelectedFilter(data.value)
          }
          }
        />
        }
      </Form.Group>
      <FormProvider {...formMethods}>
        <div ref={recipientsRef}>
          <Recipients
            isDraft={isDraft}
            filterModules={filterModules.length > 1 ? [filterModules.find(module => module.slug === selectedFilter)] : filterModules}
            selectedFilter={selectedFilter}
          />
        </div>
        {renderTabs()}
      </FormProvider>
    </>;
  }

  function renderActions() {
    return <>
      {startingDraftValue ? <SaveDraftButton
        disabled={isBusy}
        type={'submit'}
        onClick={() => setValue('isDraft', 1)}
        content={t('Save draft')}
      /> : null}
      <El.PageHeaderActionButton
        disabled={isBusy}
        onClick={() => setValue('isDraft', 0)}
        content={watchedSchedule === 1 ? t('Plan') : t('Send')}
        type={'submit'}
      />
    </>;
  }

  return <Layout
    isBusy={isBusy}
    title={id ? t('Edit newsletter') : t('Create new newsletter')}
    segmented
    onSubmit={handleSubmit(onSubmit)}
    close={RouteTypes.NEWSLETTER}
    actions={renderActions()}
  >
    {!isBusy && renderForm()}
    {renderSendNewsLetterModal()}
  </Layout>;
};
export default CreateEditNewsletter;
